import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ViewChild, ElementRef } from '@angular/core';
import { Observable, of, Subscription, Subject } from 'rxjs';
import * as FilterPanelTypes from '../../sd-filter-panel-module/types/types';
import { SdFilterPanelInputType, FilterPanelCloseType } from '../../sd-filter-panel-module/types/constants';
import { Store } from '@ngrx/store';
import { AppState } from '../../../_store/app-state.model';
import { AppSelectors } from '../../../_store/selector-types';
import { ActivatedRoute } from '@angular/router';
import { tap, switchMap, map, filter, startWith } from 'rxjs/operators';
import { ValueConverter } from '../filter-value-converter';
import { FilterSelectors, FilterActions } from '../store';
import { LocaleService } from '../../locale/locale.service';
import { SharedTranslationService } from '../../locale/translation/shared-translation.service';
import { FilterService } from '../../services/services-index';
import { HyFilterPanel } from '../../hy-filter-panel-module/types/filter-panel';
import { FilterPanelParams, IPanelInputData } from '../../hy-filter-panel-module/types/types';
import { IFacetedFilterData, IFacetedFilterDataItem, IFacetedFilterSelection, ISelectedDateRangeInfo, ISelectedFacetInfo, ISelectedHierarchyInfo, ISelectionInfo } from '../../hy-filter-panel-module/types';
import { DisplayValue, TypeCheck } from '../../hy-filter-panel-module/Utils';
import { buildReportViewFilterSelection, ReportFilterItem, ReportViewFilter, ReportViewFilterSelection } from '../../models/report-view.models';
import { AppInsightsService } from '../../services/appInsights/appInsights.service';

@Component({
    selector: 'app-report-filter-bar',
    templateUrl: './report-filter-bar.component.html',
    styleUrls: ['./report-filter-bar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ReportFilterBarComponent implements OnInit, OnDestroy {
  reportName: string;
  locale: string;
  valueConverter: ValueConverter;
  destroy$ = new Subject<boolean>();

  reportViewFilterSelections: ReportViewFilterSelection[] = [];
  reportViewFilterSelections$: Observable<ReportViewFilterSelection[]>;
  reportViewFilters: ReportViewFilter[] = [];
  reportViewFilters$: Observable<ReportViewFilter[]>;

  isLoadingFilters$: Observable<boolean>;

  @ViewChild('addFilterButton') addFilterButton: ElementRef;

  constructor(
    private store$: Store<AppState>,
    private route: ActivatedRoute,
    private filterService: FilterService,
    private filterPanelRef: HyFilterPanel,
    private localeService: LocaleService,
    private translationService: SharedTranslationService,
    private appInsights: AppInsightsService
  ) {
    this.valueConverter = new ValueConverter();
  }

  ngOnInit() {
    const route$ = this.store$.select(AppSelectors.selectCurrentRouteData).pipe(
      tap(routeData => {
        this.reportName = routeData.reportName;
      })
    );

    this.reportViewFilters$ = route$.pipe(
      switchMap(routeData => this.store$.select(FilterSelectors.selectReportViewFiltersForReport({ reportName: routeData.reportName }))
        .pipe(
          tap(filters => this.reportViewFilters = filters)
        )));

    this.reportViewFilterSelections$ = route$.pipe(
      switchMap(routeData => this.store$.select(FilterSelectors.selectReportViewFilterSelections({ reportName: routeData.reportName }))
        .pipe(
          tap(selections => {
            this.reportViewFilterSelections = selections;
          }),
          //tap(selections => console.log('Selections', selections))
        )));

    this.localeService.locale$.pipe(
      tap(locale => this.locale = locale)
    ).subscribe();

    this.isLoadingFilters$ = this.reportViewFilterSelections$.pipe(
      map(selections => !selections || selections.length === 0),
      startWith(true) // Start with true to show placeholders initially
    );
  }

  translateLabel = (val) => this.translationService.getLabelTranslation(val, this.locale);

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  onFilterClicked($event: MouseEvent, chip: ElementRef, selection: ReportViewFilterSelection) {
    this.show(chip, selection.name, selection.item);
  }

  show(origin: ElementRef, name: string, selected: IFacetedFilterSelection) {
    this.showFilterPanel(origin, selected);
  }

  showFilterPanel(originElement: ElementRef, selected?: IFacetedFilterSelection): void {
    const selectedFilterName = selected ? selected.path?.split('|')[0] : undefined;
    const selectedFilter = selectedFilterName
      ? this.reportViewFilters.find(f => f.name === selectedFilterName || f.label === selectedFilterName)
      : undefined;

    this.appInsights.trackReportFilterOpened({
      ReportKind: 'PrebuiltReport',
      ReportName: this.reportName,
      ReportType: 'Prebuilt',
      FilterName: selectedFilter?.name,
      FilterLabel: selectedFilter?.label,
      FilterType: selectedFilter?.type,
      FilterCount: this.reportViewFilterSelections?.length || 0
    });

    //transform to input data
    const inputData = this.reportViewFilters$.pipe(map(filters => this.transformToInputData(filters)));

    //console.log('>>>>showFilterPanel.selected', selected);

    const panelParams: FilterPanelParams = {
      originElement,
      inputDataType: 'faceted_filter',
      config: {
        type: 'faceted_filter',
        panelTitle: 'Filter',
        panelClass: 'lrgPanel',
        category: '',
        target: 'panel',
        inputData: inputData,
        inputFacetData: selected
      }
    };

    this.filterPanelRef.open(panelParams).afterClosed$.pipe(
      filter(({ dialogData }) => dialogData.resultType === FilterPanelCloseType.applied),
      map(({ dialogData }) => dialogData.data[0] as IFacetedFilterSelection)
    ).subscribe(dialogSelection => {
      //console.log('ReportViewFilters', this.reportViewFilters);
      //console.log('$ [Next Gen Panel] afterClosed$', { selected, dialogSelection });

      if (!dialogSelection)
        return;


        // This is really clunky right now, we'll use the first part of the path to find the filter
        const pathRoot = dialogSelection.path.split('|')[0];

        const filter = this.reportViewFilters.find(f => f.name === pathRoot || f.label === pathRoot);
        const existingSelection = this.reportViewFilterSelections.find(s => s.name === filter.name);

        let selectionToAdd: ReportViewFilterSelection;

        if (existingSelection) {
          selectionToAdd = {...existingSelection, item: dialogSelection};
        } else {
          selectionToAdd = buildReportViewFilterSelection(filter.name, filter.label, dialogSelection, filter.locked, filter.lockable, filter.removeable, filter.visible);
        }

        //console.log('Selection to Add', selectionToAdd);

        const index = this.reportViewFilterSelections.findIndex(s => s.name === filter.name);

        let updateSelections;
        if (index >= 0) {
            // Copy the array and replace the item at the found index
            updateSelections = [...this.reportViewFilterSelections];
            updateSelections[index] = selectionToAdd;
        } else {
            // Item not found, add to the end of the array
            updateSelections = [...this.reportViewFilterSelections, selectionToAdd];
        }

        this.store$.dispatch(FilterActions.updateReportViewFilterSelections({ reportName: this.reportName, selections: updateSelections }));
        this.appInsights.trackReportFilterApplied({
          ReportKind: 'PrebuiltReport',
          ReportName: this.reportName,
          ReportType: 'Prebuilt',
          FilterName: filter?.name,
          FilterLabel: filter?.label,
          FilterType: filter?.type,
          FilterCount: updateSelections.length,
          SelectionCount: this.getSelectionCount(dialogSelection)
        });
    })
  }

  private transformToInputData(filters: ReportViewFilter[]): IPanelInputData {
    //const items = filters.map(filter => filter.items?.map(item => this.transformItemToDataItem(item)));
    const items: IFacetedFilterDataItem[] = [];



    filters.forEach(filter => {
      items.push({
        label: filter.label ?? filter.name,
        name: filter.name,
        type: filter.type === "multi-select-hierarchy" ? "hierarchy-facet-container" : "container",
        path: filter.label ?? filter.name,
        searchable: false,
        items: filter.items?.map(item => this.transformItemToDataItem(item))
      });
    });

    const inputData = <IFacetedFilterDataItem>{
      label: 'Filter',
      items: items
    };
    //console.log('Filter Items', items);
    return { inputData: items };
  }

  private transformItemToDataItem(item: ReportFilterItem): IFacetedFilterDataItem {
    return {
      label: item.label ?? item.name,
      name: item.name,
      type: item.type,
      filterType: item.filterType,
      path: item.path,
      value: item.value,
      valueType: item.valueType,
      searchable: item.searchable,
      items: item.items?.map(item => this.transformItemToDataItem(item))
    }
  }

  lockFilter(selection: ReportViewFilterSelection) {
    const newSelections = this.reportViewFilterSelections.map(s => {
      if (s.name === selection.name) {
        return { ...s, locked: !s.locked };
      }

      return s;
    });

    this.store$.dispatch(FilterActions.updateReportViewFilterSelections({ reportName: this.reportName, selections: newSelections }));
    this.appInsights.trackReportFilterLockToggled({
      ReportKind: 'PrebuiltReport',
      ReportName: this.reportName,
      ReportType: 'Prebuilt',
      FilterName: selection.name,
      FilterLabel: selection.label,
      IsLocked: !selection.locked
    });
  }

  removeFilter(selection: ReportViewFilterSelection) {
    // remove from selections
    if (!selection.removeable)
      return;

    const updatedSelections = this.reportViewFilterSelections.filter(s => s.name !== selection.name);
    this.store$.dispatch(FilterActions.updateReportViewFilterSelections({ reportName: this.reportName, selections: updatedSelections }));
    this.appInsights.trackReportFilterRemoved({
      ReportKind: 'PrebuiltReport',
      ReportName: this.reportName,
      ReportType: 'Prebuilt',
      FilterName: selection.name,
      FilterLabel: selection.label,
      FilterCount: updatedSelections.length
    });
  }

  resetFilters() {
    this.store$.dispatch(FilterActions.resetReportViewFilters({ reportName: this.reportName }));
    this.appInsights.trackReportFiltersReset({
      ReportKind: 'PrebuiltReport',
      ReportName: this.reportName,
      ReportType: 'Prebuilt',
      FilterCount: this.reportViewFilterSelections?.length || 0
    });
  }

  getDisplayValue(selection: ReportViewFilterSelection, locale?: string): string {
    if (TypeCheck.isDateRangeValue(selection.item.value)) {
      return DisplayValue.FromDateRangeSelection({
        dateRange: selection.item.value,
        item: selection.item as IFacetedFilterDataItem
      }, locale ?? this.localeService.locale ?? this.localeService.getDefaultLocale().locale);
    }

    return selection.item.displayValue ?? selection.item.name;
  }

  private getSelectionCount(selection: IFacetedFilterSelection): number {
    const value = selection?.value;
    return Array.isArray(value) ? value.length : value ? 1 : 0;
  }
}
